import {
  LogoFileExtntionRequest,
  LogoFileExtntionResponse,
} from "../../views/Profile/AddRestaurant";
import { apiSlice } from "../apiSlice";
const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PROD;

export interface PortionDetails {
  portionSize: string;
  itemCount: number;
}

export interface DimensionValue {
  id: string;
  dimensionId: string;
  displayName: string;
  description: string;
  displayOrder: number;
  createdAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  imageUrl?: string;
}

export interface ProductDimension {
  maxValuesAllowed: string;
  id: string;
  customMenuItemTypeId: string;
  displayName: string;
  selectionOrder: number;
  isRequired: boolean;
  createdAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  dimensionValue?: DimensionValue[]; // Note: API returns 'dimensionValue' (singular)
}

export interface CustomTypeResponse {
  id: string;
  name: string;
  hasMultipleDimensions: boolean;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  availableCount: number;
  totalCount: number;
}

export interface SaveCustomizeMenuTypeRequest {
  restaurantId: string;
  name: string;
  hasMultipleDimensions: boolean;
  customMenuItemTypeId?: string;
}

export interface SaveDimensionRequest {
  restaurantId: string;
  customMenuItemTypeId: string;
  displayName: string;
  selectionOrder: number;
  isRequired: boolean;
  dimensionId?: string;
}

export interface SaveDimensionValueRequest {
  restaurantId: string;
  description: string;
  dimensionId: string;
  displayName: string;
  displayOrder: number;
  dimensionValueId?: string;
}
export interface UpdateDimensionValueImageRequest {
  imgRef: string;
  restaurantId: string;
  dimensionValueId: string;
}

export const menuTypeService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createType: builder.mutation<any, SaveCustomizeMenuTypeRequest>({
      query: (data) => ({
        url: `https://adminhttps-savecustomitemtypes${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.json(),
      }),
    }),

    getMenuTypes: builder.query<CustomTypeResponse[], { restaurantId: string }>(
      {
        query: ({ restaurantId }) => ({
          url: `https://adminhttps-getcustomitemtypesforrestaurant${API_BASE_DOMAIN}/?restaurantId=${restaurantId}`,
          method: "GET",
        }),
        providesTags: (result, error, { restaurantId }) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "MenuTypes" as const, id })),
                { type: "MenuTypes", id: restaurantId },
              ]
            : [{ type: "MenuTypes", id: restaurantId }],
      }
    ),

    deleteType: builder.mutation<
      any,
      {
        restaurantId: string;
        customMenuType: string;
        customMenuItemTypeId?: string;
      }
    >({
      query: (data) => ({
        url: `https://adminhttps-deletecustommenuitemtype${API_BASE_DOMAIN}/`,
        method: "DELETE",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    createPortion: builder.mutation<
      any,
      { restaurantId: string; portionSize: string }
    >({
      query: (data) => ({
        url: `https://adminhttps-createportionsizelist${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    getPortionSizes: builder.query<PortionDetails[], { restaurantId: string }>({
      query: ({ restaurantId }) => ({
        url: `https://adminhttps-getportionsizeslist${API_BASE_DOMAIN}/?restaurantId=${restaurantId}`,
        method: "GET",
      }),
    }),

    deletePortion: builder.mutation<
      any,
      { restaurantId: string; portionSize: string }
    >({
      query: (data) => ({
        url: `https://adminhttps-deleteportionsize${API_BASE_DOMAIN}/`,
        method: "DELETE",
        body: data,
        responseHandler: (response) => response.text(),
      }),
    }),

    // Dimension Management APIs
    saveDimension: builder.mutation<any, SaveDimensionRequest>({
      query: (data) => ({
        url: `https://adminhttps-savedimension${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (
        result,
        error,
        { restaurantId, customMenuItemTypeId }
      ) => [
        { type: "Dimensions", id: `${restaurantId}-${customMenuItemTypeId}` },
      ],
    }),

    getDimensions: builder.query<
      ProductDimension[],
      { restaurantId: string; customMenuItemTypeId: string }
    >({
      query: ({ restaurantId, customMenuItemTypeId }) => ({
        url: `https://adminhttps-getdimensions${API_BASE_DOMAIN}/?restaurantId=${restaurantId}&customMenuItemTypeId=${customMenuItemTypeId}`,
        method: "GET",
      }),
      providesTags: (result, error, { restaurantId, customMenuItemTypeId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Dimensions" as const, id })),
              {
                type: "Dimensions",
                id: `${restaurantId}-${customMenuItemTypeId}`,
              },
            ]
          : [
              {
                type: "Dimensions",
                id: `${restaurantId}-${customMenuItemTypeId}`,
              },
            ],
    }),

    deleteDimension: builder.mutation<
      any,
      { restaurantId: string; dimensionId: string }
    >({
      query: (data) => ({
        url: `https://adminhttps-deleteDimension${API_BASE_DOMAIN}/`,
        method: "DELETE",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, { restaurantId, dimensionId }) => [
        { type: "Dimensions", id: dimensionId },
        "Dimensions",
      ],
    }),

    // Dimension Value Management APIs
    saveDimensionValue: builder.mutation<any, SaveDimensionValueRequest>({
      query: (data) => ({
        url: `https://adminhttps-savedimensionvalue${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, { restaurantId, dimensionId }) => [
        { type: "Dimensions", id: dimensionId },
        "Dimensions",
      ],
    }),

    deleteDimensionValue: builder.mutation<
      any,
      { restaurantId: string; dimensionValueId: string }
    >({
      query: (data) => ({
        url: `https://adminhttps-deletedimensionvalue${API_BASE_DOMAIN}/`,
        method: "DELETE",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, { restaurantId }) => [
        "Dimensions",
        { type: "Dimensions", id: restaurantId },
      ],
    }),
    UploadSetDimensionLogoFileExtntionHttps: builder.mutation<
      LogoFileExtntionResponse,
      LogoFileExtntionRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-getdimensionvalueimageuploadurl${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
      }),
    }),

    updateDimensionValueImage: builder.mutation<
      any,
      UpdateDimensionValueImageRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-updatedimensionvalueimage${API_BASE_DOMAIN}/`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, { restaurantId, dimensionValueId }) => [
        { type: "Dimensions", id: `${restaurantId}-${dimensionValueId}` },
        "Dimensions",
      ],
    }),
  }),
});

export const {
  useCreateTypeMutation,
  useGetMenuTypesQuery,
  useLazyGetMenuTypesQuery,
  useDeleteTypeMutation,
  useCreatePortionMutation,
  useGetPortionSizesQuery,
  useLazyGetPortionSizesQuery,
  useDeletePortionMutation,
  useSaveDimensionMutation,
  useGetDimensionsQuery,
  useLazyGetDimensionsQuery,
  useDeleteDimensionMutation,
  useSaveDimensionValueMutation,
  useDeleteDimensionValueMutation,
  useUploadSetDimensionLogoFileExtntionHttpsMutation,
  useUpdateDimensionValueImageMutation,
} = menuTypeService;
